/**
 * Component Registry for Aion Management Theme
 *
 * Extends the base stadium-house theme registry with Aion Management specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-stadium-house/registry';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { HeroSolidBG } from './components/HeroSolidBG';
import { FloorplansList } from './components/FloorplansList';
import { PropertyOverview } from './components/PropertyOverview';
import { AmenitiesForYou } from './components/AmenitiesForYou';
import { AmenitiesList } from './components/AmenitiesList';
import { NeighborhoodHighlights } from './components/NeighborhoodHighlights';
import { NeighborhoodMap } from './components/NeighborhoodMap';
import { PhotoGallery } from './components/PhotoGallery';
import { PetPolicyCallout } from './components/PetPolicyCallout';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';
import { Footer } from './components/Footer';
import { ContactForm } from './components/ContactForm';
import { ScheduleTourForm } from './components/ScheduleTourForm';
import { FAQList } from './components/FAQList';
import { ThankYouContent } from './components/ThankYouContent';
import { PrivacyPolicyContent } from './components/PrivacyPolicyContent';
import { AccessibilityStatement } from './components/AccessibilityStatement';
import { HyperlocalHero } from './components/HyperlocalHero';
import { HyperlocalPageCopy } from './components/HyperlocalPageCopy';
import { ScrollingText } from './components/ScrollingText';
import { ResidentAccess } from './components/ResidentAccess';
import { SitemapContent } from './components/SitemapContent';
import { FeeTransparency } from './components/FeeTransparency';
import { FeeSheet } from './components/FeeSheet';
import { InteractiveMapSection } from './components/InteractiveMapSection';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { ApartmentSyncSection } from './components/ApartmentSyncSection';

/**
 * Aion Management specific component overrides.
 * These components have been customized for the Aion Management brand.
 *
 * - Header: Aion layout — logo pinned to the LEFT edge, all nav links
 *   right-aligned in a single group ending with a "SCHEDULE A TOUR" CTA.
 *   Overrides the Stadium House default (centered logo, nav split around it).
 *   Adds a secondary-colored utility top bar above the pill nav (address,
 *   phone, Resident Portal, Apply Now) with luminance-based black/white
 *   contrast; hidden on tablet and smaller desktops (below 1280px),
 *   address-only on mobile.
 * - HeroSolidBG: Aion inner-page hero — solid dark-navy background panel
 *   with a centered page title and optional blurb heading + blurb stacked
 *   underneath (no photographic background, no eyebrow/headline, no in-hero
 *   CTA). Per-page pages.json data (headline, cta_link, bg_image, phone/email
 *   CTAs) is preserved in the schema so the older look can be restored
 *   without data loss.
 * - Hero: three Aion tweaks over the base theme —
 *   (1) the title `max-width` opens up on tablet/desktop so the default title
 *       fits on one line; longer custom titles still wrap gracefully (balanced)
 *       to two lines.
 *   (2) the bottom quick-link cards use a primary-color overlay at 50% opacity
 *       instead of the base theme's dark rgba wash, so they read as branded
 *       chips rather than neutral glass tiles.
 *   (3) adds an editable <h2> subtitle beneath the <h1> title, with default
 *       field tokens for bedrooms range, city, and state.
 * - FloorplansList: full-bleed featured-image background with an ~80%
 *   primary-color overlay and centered copy; floor-plan cards render as
 *   large numeric labels grouped from the live floor plan collection.
 * - PropertyOverview: single hero photo on the right (Aion prefers a clean
 *   full-image treatment over the base theme's two overlapping cards).
 *   Retains the interior-photo ranking so the picked shot avoids logos,
 *   floorplans, bathrooms, and utility spaces.
 * - AmenitiesForYou: simplified two-column layout — photo on the left,
 *   heading/body/CTA on the right. Removes the base theme's offset
 *   primary-color accent rectangle behind the photo and drops the
 *   interactive 6-tile amenity grid so the section reads as a clean
 *   pitch card with a single "View All Amenities" CTA.
 * - AmenitiesList: compact text list grid on the shared warm #EDEBEB
 *   surface — one column per amenity group (Indoor, In-Home, Outdoor,
 *   Neighborhood) with a left-aligned h3 heading and bulleted list.
 *   Drops the prior photo-slider rows for a less image-heavy Amenities page.
 * - NeighborhoodMap: same layout as the base theme's split map + POI panel,
 *   but the POI list-item hover state uses a fixed Aion brand orange
 *   (#e2903d) instead of the property secondary color, giving the hover a
 *   consistent warm accent regardless of the property palette. The
 *   active/selected state still uses the property secondary color so hover
 *   and active remain visually distinct.
 * - NeighborhoodHighlights: replaces the parent theme's peek-carousel of
 *   themed stock cards with an Aion take on the Verso "Tour the
 *   neighborhood" pattern — a primary-color band, a white BY DAY / BY NIGHT
 *   toggle pill, and two horizontal card sliders. Each card is an editable
 *   slot group defaulting to a `neighborhood_pois` entry so editors can
 *   hand-pick which spots headline daytime vs. nighttime moods; night cards
 *   swap to a lightened-primary surface so the two states read distinctly.
 * - PhotoGallery: simple responsive grid (1 / 2 / 3 columns) of all
 *   property photos with rounded-corner tiles. Drops both the base
 *   Stadium House gallery's Community / Apartment filter chips and its
 *   alternating 2×2 + tall-portrait mosaic; the shared lightbox is still
 *   what opens on click.
 * - PetPolicyCallout: replaces the parent theme's three-image
 *   "Learn more about our pets policy" strip with a single dark
 *   two-panel card — copy on the left (primary panel, heading + blurb +
 *   prominent pet-policy stats) and one large photo on the
 *   right, wired through the photo_gallery collection. Sits at the bottom
 *   of the Amenities page as a bolder brand-colored pitch card.
 * - VirtualTourEmbed: same rendering as the parent theme, plus a
 *   client-side guard that redirects /virtual-tour → /floor-plans when
 *   the property's virtual_tours collection is empty. The nav and sitemap
 *   already omit the link for those properties; this closes the
 *   direct-URL loophole so bookmarked or cached /virtual-tour URLs don't
 *   land on an empty page. SSR and the inline editor are unaffected —
 *   only real-browser visits redirect.
 * - Footer: Aion-branded footer that drops the base theme's "Live your
 *   best life at ..." CTA banner, "Residents" apply/login column, and
 *   "Ready to See It in Person?" leasing card in favor of a leaner
 *   layout — logo + socials, Contact/Visit/Hours columns, an emergency
 *   contact block with an optional NJ 2-1-1 badge, and a bottom
 *   attribution row with "Managed by AION" left, compliance icons +
 *   copyright + legal links center, and Repli on the right. No separate
 *   primary-color strip; the whole footer sits on the navy secondary.
 * - ContactForm: same two-column layout as the parent theme, with the
 *   embedded multihub-lead-form card title hidden (the section already
 *   has a Contact Us heading) and the submit CTA hover swapped from
 *   secondary to the Aion orange surface accent. Title uses normal
 *   (400) font weight.
 * - ScheduleTourForm: parent layout with normal (400) title weight to
 *   match ContactForm.
 * - FAQList: softens the base theme's sharp accordion bars — 12px rounded
 *   items, pill category chips, and the same warm #EDEBEB answer panel used
 *   on AmenitiesList so the FAQ page sits closer to the Aion surface language.
 * - ThankYouContent: keeps the parent thank-you copy + dual CTAs, but remaps
 *   the filled secondary CTA to the Aion primary → surface (orange) hover
 *   used on PropertyOverview, AmenitiesForYou, and ContactForm.
 * - PrivacyPolicyContent: same layout as the parent, but body copy uses
 *   --vibe-color-text (matching HeroSolidBG blurb) instead of the parent's
 *   muted text color.
 * - AccessibilityStatement: same layout as the parent, but body copy uses
 *   --vibe-color-text (matching HeroSolidBG blurb) instead of the parent's
 *   muted text color.
 * - HyperlocalHero: same Stadium House hero, but title, subtitle, and
 *   background image default from the selected hyperlocal_queries row
 *   (h1_title, h2_subtitle, featured_image) via useTemplateContext().
 * - HyperlocalPageCopy: body copy typography matches HeroSolidBG blurb
 *   (font-body, text-base md:text-lg, leading-relaxed, --vibe-color-text)
 *   instead of the parent's muted text at base size only. Page copy defaults
 *   from the selected query's page_copy.
 * - ScrollingText: same marquee as the parent, with amenity divider dots at
 *   full opacity instead of the base theme's 50%.
 * - ResidentAccess: Aion layout matching the reference — two bordered
 *   panels (Resident Portal login + phone) without icons/descriptions,
 *   plus a Loft mobile-app download row with App Store / Google Play badges.
 * - SitemapContent: lists the same pages as the Aion Header nav (local
 *   pages.json `visible` flag, flat — no nested `parent` pages), so the
 *   sitemap no longer pulls the parent theme's hierarchical page list
 *   (e.g. Pet Policy / FAQ children that Header hides).
 * - FeeSheet: Engrain catalog (`feesheet.*`) on Floor Plans and hidden
 *   /fee-information. FeeTransparency stays in the repo; its overrides are unused.
 * - SpecialOfferBanner: Prestige-style promotional strip (secondary surface,
 *   Apply Now CTA, collection-bound offers) without role="banner" so the
 *   site Header remains the only banner landmark.
 * - ApartmentSyncSection: parent wrapper around the apartment-sync block,
 *   plus a white section background and top padding (token xl → 2xl) so
 *   the Floor Plans widget is no longer flush against the HeroSolidBG
 *   page header.
 */
const aionmanagementOverrides: Partial<ComponentRegistry> = {
  Header,
  Hero,
  HeroSolidBG,
  FloorplansList,
  PropertyOverview,
  AmenitiesForYou,
  AmenitiesList,
  NeighborhoodHighlights,
  NeighborhoodMap,
  PhotoGallery,
  PetPolicyCallout,
  VirtualTourEmbed,
  Footer,
  ContactForm,
  ScheduleTourForm,
  FAQList,
  ThankYouContent,
  PrivacyPolicyContent,
  AccessibilityStatement,
  HyperlocalHero,
  HyperlocalPageCopy,
  ScrollingText,
  ResidentAccess,
  SitemapContent,
  FeeTransparency,
  FeeSheet,
  InteractiveMapSection,
  SpecialOfferBanner,
  ApartmentSyncSection,
};

/**
 * Final merged registry for Aion Management properties.
 * Inherits all components from the stadium-house theme, with Aion Management overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...aionmanagementOverrides,
};

export default registry;
